"use client";

import * as React from "react";
import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
const faqs = [{
  question: "What is the difference between cast acrylic and extruded acrylic for signs?",
  answer: "Cast acrylic is produced by pouring liquid monomer into a mold, which results in a more consistent thickness, better optical clarity, and superior engraving quality. Extruded acrylic is pushed through a die and is less dimensionally consistent. Evermark Industrial uses cast acrylic sheet for all engraving and sign production — extruded acrylic does not produce clean engraved edges and is not recommended for engraved nameplates or industrial signs."
}, {
  question: "Can acrylic signs be used outdoors?",
  answer: "Cast acrylic has good UV resistance compared to other plastics and is commonly used for outdoor signs in covered or semi-exposed locations. For fully outdoor, direct-sun applications, Evermark recommends discussing the environment before ordering — clear and lightly colored acrylic holds up better than dark colors in direct UV exposure, and engraved text remains legible longer than printed text in outdoor conditions. For harsh outdoor industrial environments, anodized aluminum or stainless steel nameplates are typically a better choice."
}, {
  question: "What is the minimum thickness for laser-cut acrylic parts?",
  answer: 'Evermark Industrial can laser-cut cast acrylic from 1/16" minimum thickness for thin nameplate and label applications up to 1/2" for structural sign panels and display components. Thinner materials require careful nesting and handling — contact Evermark to confirm suitability for your specific application.'
}, {
  question: "Do Evermark acrylic nameplates meet UL 508A requirements?",
  answer: "Two-color acrylic engraving stock meets the visual contrast and legibility requirements of UL 508A §49.6 for panel nameplate identification in most control panel applications. However, UL 508A §49.6 specifies phenolic (Grade CE) as the preferred engraving material for nameplates in panels where dielectric properties matter. For panels where the nameplate may contact live conductors or busbars, phenolic is the required material. Evermark recommends confirming material requirements with your UL inspector before specifying acrylic in place of phenolic for UL 508A-listed panels."
}, {
  question: "Can I order a single acrylic sign or a small quantity?",
  answer: "Yes. Evermark Industrial does not require a minimum order quantity on acrylic signs or cut parts. A single replacement sign, one custom-cut acrylic panel, or a short run of five to ten pieces are all quoted from the same price list as production orders. There are no setup fees on reorders of previously produced designs."
}, {
  question: "What file format should I use for a custom-cut acrylic shape?",
  answer: "DXF is the preferred format for cut profiles — it translates directly to the laser cutter without interpretation. Adobe Illustrator (AI) and SVG files are also accepted. PDF files can be used for engraving artwork. If your file contains both a cut profile and engraving artwork, submit them on separate layers clearly labeled. Evermark's team reviews every file before quoting and will flag any issues that could affect cut quality or engraving registration."
}];
export default function AcrylicFaq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="acrylicservicesfaq" className="bg-background py-16 md:py-24 lg:py-32">
      <div className="mx-auto max-w-4xl px-6">
        <motion.div ref={ref} initial={{
        opacity: 0,
        y: 20
      }} animate={isVisible ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="mb-12 text-center md:mb-16">
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground md:text-4xl">
            {"Frequently Asked Questions — Acrylic Signs and Cutting"}
          </h2>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isVisible ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.6,
        ease: "easeOut",
        delay: 0.2
      }}>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-border">
                <AccordionTrigger className="font-default text-left text-lg font-semibold text-foreground hover:text-primary">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="font-sans text-base leading-relaxed text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </motion.div>
      </div>
    </section>;
}